import {
  createContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "../supabase";

export interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signInWithMagicLink(email: string): Promise<{ error: Error | null }>;
  signInWithGoogle(): Promise<void>;
  signOut(): Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

// A fake session used only when VITE_E2E_BYPASS_AUTH=true so Playwright can
// drive the app without going through Supabase. Shape kept minimal — it is
// only inspected by AuthGate / consumers that look at `user.id`.
const FAKE_USER = {
  id: "00000000-0000-0000-0000-000000000000",
  app_metadata: {},
  user_metadata: {},
  aud: "authenticated",
  created_at: new Date(0).toISOString(),
} as unknown as User;

const FAKE_SESSION = {
  access_token: "e2e",
  refresh_token: "e2e",
  expires_in: 3600,
  expires_at: Math.floor(Date.now() / 1000) + 3600,
  token_type: "bearer",
  user: FAKE_USER,
} as unknown as Session;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(
    E2E_BYPASS ? FAKE_SESSION : null,
  );
  const [loading, setLoading] = useState<boolean>(!E2E_BYPASS);

  useEffect(() => {
    if (E2E_BYPASS) return;

    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
      });

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        setSession(nextSession);
        setLoading(false);
      },
    );

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      async signInWithMagicLink(email: string) {
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/auth/callback`,
          },
        });
        return { error: error ?? null };
      },
      async signInWithGoogle() {
        // Bypass mode: mirror the magic-link short-circuit. Flip the local
        // state to the fake session so <AuthGate> unlocks immediately without
        // ever touching supabase.auth.signInWithOAuth (which would redirect
        // the browser away from the Playwright-controlled page).
        if (E2E_BYPASS) {
          setSession(FAKE_SESSION);
          setLoading(false);
          return;
        }
        const { error } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
            queryParams: { access_type: "offline", prompt: "consent" },
          },
        });
        if (error) throw error;
        // Supabase redirects the browser — no return value.
      },
      async signOut() {
        if (E2E_BYPASS) return;
        await supabase.auth.signOut();
      },
    }),
    [session, loading],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}
